export default class UserSessionRepository {
	#httpClient;
	#endpointUrl;
	#usersUrl;

	constructor(httpClient, endpointUrl = '/api/admin/user-sessions', usersUrl = '/api/admin/users') {
		this.#httpClient = httpClient;
		this.#endpointUrl = endpointUrl;
		this.#usersUrl = usersUrl;
	}

	/**
	 * Every user account with its active flag, whether it's ENV-managed, and its active session count.
	 * @returns {Promise<{name: string, firstname: string, lastname: string, email: string, active: boolean,
	 *          envManaged: boolean, numberOfActiveSessions: number}[]>}
	 */
	async getUsers() {
		return  await this.#httpClient
			.fetch(this.#usersUrl, {
				method: 'GET',
				headers: {
					"Accept": "application/json"
				},
				responseType: 'json'
			});
	}

	/**
	 * The deployment's license status. {@code expiry} ({@code status}/reason/...) drives the
	 * license-expiry banner (see {@link LicenseExpiryBannerCmp}), which derives {@code daysLeft} and the
	 * EXPIRING_SOON warning from {@code exp} itself (see {@link LicenseUtils#deriveExpiry}). Unlike
	 * {@link UserSessionRepository#getUsers}, this is available to every signed-in user, not only admins.
	 * In `expiry` only `status` is always present; `source`/`exp`/`reason` are omitted when they don't apply.
	 * @returns {Promise<{expiry: {status: string, source?: string, sub?: string, exp?: string, reason?: string}}>}
	 */
	async getLicenseStatus() {
		return await this.#httpClient
			.fetch('/api/license/status', {
				method: 'GET',
				headers: {
					"Accept": "application/json"
				},
				responseType: 'json'
			});
	}

	/**
	 * @param {string} username
	 * @param {boolean} active
	 * @returns {Promise<void>}
	 */
	async setUserActive(username, active) {
		await this.#httpClient
			.fetch(`${this.#usersUrl}/${encodeURIComponent(username)}/active`, {
				method: 'PUT',
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify({active})
			});
	}

	/**
	 * @returns {Promise<{username: string, activeSessions: number}[]>}
	 */
	async getActiveSessions() {
		const response = await this.#httpClient
			.fetch(this.#endpointUrl, {
				method: 'GET',
				headers: {
					"Accept": "application/json"
				},
				responseType: 'json'
			});
		return Object.entries(response)
			.map(([username, activeSessions]) => ({username, activeSessions}))
			.sort((a, b) => a.username.localeCompare(b.username));
	}

	/**
	 * @param {string} username
	 * @returns {Promise<void>}
	 */
	async terminateUserSessions(username) {
		await this.#httpClient
			.fetch(`${this.#endpointUrl}?username=${encodeURIComponent(username)}`, {
				method: 'DELETE'
			});
	}
}
